import { useCallback, useEffect, useState } from 'react';

export type TableDensity = 'compact' | 'standard';

const STORAGE_KEY = 'lgm-table-density';
const DEFAULT_DENSITY: TableDensity = 'compact';
/** Custom event fired whenever any hook instance changes density. */
const DENSITY_EVENT = 'lgm:density-change';

function readFromStorage(): TableDensity {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'compact' || stored === 'standard') {
      return stored;
    }
  } catch {
    // localStorage unavailable (SSR or restricted context) — fall through
  }
  return DEFAULT_DENSITY;
}

function writeToStorage(density: TableDensity): void {
  try {
    localStorage.setItem(STORAGE_KEY, density);
  } catch {
    // localStorage unavailable — ignore
  }
}

export interface UseTableDensityResult {
  density: TableDensity;
  setDensity: (density: TableDensity) => void;
}

/**
 * Global table density preference.
 *
 * Persists the user's chosen density (`'compact'` | `'standard'`) in
 * `localStorage` under the key `'lgm-table-density'`. All pages that
 * consume this hook share the same value.
 *
 * Multiple instances on the same page stay in sync via a custom window
 * event (`lgm:density-change`). When any instance calls `setDensity`,
 * all other instances receive the `CustomEvent` and update their state.
 *
 * Compact row height: 36 px
 * Standard row height: 52 px (current LgmDataGrid default)
 */
export function useTableDensity(): UseTableDensityResult {
  const [density, setDensityState] = useState<TableDensity>(DEFAULT_DENSITY);

  useEffect(() => {
    // Read persisted value after mount (avoids SSR mismatch)
    setDensityState(readFromStorage());

    // Sync with other hook instances on the same page
    function handleChange(e: Event): void {
      const next = (e as CustomEvent<TableDensity>).detail;
      setDensityState(next);
    }
    window.addEventListener(DENSITY_EVENT, handleChange);
    return () => window.removeEventListener(DENSITY_EVENT, handleChange);
  }, []);

  const setDensity = useCallback((next: TableDensity) => {
    writeToStorage(next);
    setDensityState(next);
    window.dispatchEvent(new CustomEvent(DENSITY_EVENT, { detail: next }));
  }, []);

  return { density, setDensity };
}
